/**
 * BarChart — tier-2 adapter. Covers vertical, horizontal and stacked bars,
 * which downstream were three separate Highcharts call patterns.
 */
import { useMemo } from 'react';
import { Chart, type ChartBaseProps } from './Chart';
import type { ChartConfig } from './types';

export interface BarSeries {
  /** Field name holding this series' measure. */
  key: string;
  name: string;
  color?: string;
}

export interface BarChartProps extends ChartBaseProps {
  /** Field name holding the category label. */
  xKey: string;
  series: readonly BarSeries[];
  orientation?: 'vertical' | 'horizontal';
  stacked?: boolean;
  title?: string;
}

export function BarChart({
  xKey,
  series,
  orientation = 'vertical',
  stacked = false,
  title,
  ...rest
}: BarChartProps) {
  const config: ChartConfig = useMemo(
    () => ({
      title,
      chartType: stacked ? 'stackedBar' : orientation === 'horizontal' ? 'horizontalBar' : 'bar',
      series: series.map((s) => ({
        id: s.key,
        name: s.name,
        color: s.color,
        fields: { x: xKey, y: s.key },
      })),
    }),
    [xKey, series, orientation, stacked, title],
  );
  return <Chart config={config} {...rest} />;
}
